import React from 'react';

/**
 * Clamp a raw input string to [min, max]. Empty or non-numeric input returns
 * `fallback` — so clearing a field never forces it to a bound mid-edit. This is
 * the pure core of the draft-while-focused / clamp-on-blur behavior below.
 */
export function clampToRange(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (raw.trim() === '' || Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

/**
 * Draft-while-focused / clamp-on-blur behavior for a numeric field.
 *
 * The controlled input holds a free-text string draft while focused, so every
 * keystroke is typeable; the value is clamped to [min, max] and committed
 * exactly once on blur. Empty/invalid input on blur keeps the prior committed
 * value (no data loss). This fixes the per-keystroke clamp that made only `min`
 * and `max` reachable by typing (the "solo marca 1 y 7" bug) and that silently
 * stored 18 when a user typed 8 into a 1–24 field.
 */
export function useClampedNumberField(
  committed: number,
  min: number,
  max: number,
  onCommit: (value: number) => void,
) {
  const [draft, setDraft] = React.useState<string | null>(null);
  return {
    value: draft ?? String(committed),
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setDraft(String(committed));
      e.target.select();
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value),
    onBlur: () => {
      // Clamp once on blur; empty/invalid falls back to the prior value.
      onCommit(clampToRange(draft ?? '', min, max, committed));
      setDraft(null);
    },
  };
}

export interface ClampedNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'min' | 'max' | 'onChange'> {
  value: number;
  min: number;
  max: number;
  onCommit: (value: number) => void;
  /** Element/component to render (defaults to a native <input>). Pass `as={Input}`
   *  to reuse a styled input component. */
  as?: React.ElementType;
}

/**
 * Drop-in numeric input that drafts while focused and clamps on blur. Extra
 * props (className, step, inputMode, placeholder…) pass straight through to the
 * rendered element. Written with createElement so this stays a plain .ts util.
 */
export function ClampedNumberInput({ value, min, max, onCommit, as = 'input', ...rest }: ClampedNumberInputProps) {
  const field = useClampedNumberField(value, min, max, onCommit);
  // Girar la rueda sobre un campo con foco movía su valor en vez de mover la
  // página: la página parecía trabada y el número cambiaba sin que se viera.
  // Al soltar el foco, el `blur` de arriba confirma lo que ya estaba escrito.
  const onWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (document.activeElement === e.currentTarget) e.currentTarget.blur();
  };
  // `onWheel` va DESPUÉS de `...rest`: si fuera antes, cualquier llamada que
  // pase su propio onWheel lo pisaría y volvería la trampa de la rueda.
  return React.createElement(as, { type: 'number', min, max, ...rest, ...field, onWheel });
}
